import { SUBSCRIPTION_CONFIG } from '@hero/core';
import { useSubscriptionMineApi } from '@hero/api-hooks';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Render flavour the badge should show. Lets the component pick a
 * translation key + colour without recomputing dates itself.
 *
 * - `'trial_ended'` — `trialing` subscription whose `trialEndsAt` has
 *   elapsed. Backend will flip status on the next cron sweep; surfacing
 *   to the user proactively avoids the "trial silently expired" trap.
 * - `'period_ended'` — `active` subscription whose `currentPeriodEnd`
 *   has elapsed but the cron has not yet attempted (or has not yet
 *   succeeded at) a renewal. Amber.
 * - `'past_due_grace'` — `past_due` subscription still within
 *   `pastDueSince + graceDays`. Amber.
 * - `'past_due_overdue'` — `past_due` subscription whose grace has
 *   elapsed; the platform paywall (`useCanUseAppGuard`) is about to
 *   kick in. Red.
 *
 * Note on countdowns: we deliberately do NOT surface a "X days left"
 * countdown anywhere user-facing. Behavioural reasoning — countdowns
 * read as "you have time to pay later" and users defer. A flat
 * "your subscription has expired, pay now" framing converts better.
 * If admin tooling ever needs the count, compute it from `graceEndsAt`.
 */
export type SubscriptionRenewalReason =
  | 'trial_ended'
  | 'period_ended'
  | 'past_due_grace'
  | 'past_due_overdue';

export interface SubscriptionRenewalGuardState {
  /**
   * `true` when the user should be nudged to renew. `false` short-circuits
   * the badge — callers can `if (!shouldRenew) return null`.
   *
   * Single trigger: `Subscription.currentPeriodEnd < now` (or
   * `trialEndsAt < now` for trialing subs). We do NOT light up while the
   * period is still live; this keeps the badge useful and avoids cry-wolf.
   */
  shouldRenew: boolean;
  /** Amber for soft, red for hard. Drives the badge's colour tokens. */
  severity: 'soft' | 'hard';
  /**
   * Concrete cutoff after which the paywall kicks in, when the sub is
   * `past_due`. Retained on the public type so admin tooling / debug
   * surfaces can render it; the dashboard's user-facing copy never
   * shows it (see "no countdown" note above).
   */
  graceEndsAt: Date | undefined;
  /** Discriminator for the badge's translation key — see type above. */
  reason: SubscriptionRenewalReason | undefined;
}

const INACTIVE: SubscriptionRenewalGuardState = {
  shouldRenew: false,
  severity: 'soft',
  graceEndsAt: undefined,
  reason: undefined,
};

/**
 * Interim renewal-nudge manager. Until Moyasar tokenization (Option 4)
 * is shipped, every renewal cycle requires the user to manually pay
 * the freshly-minted Billing — see
 * `docs/adr/001-moyasar-tokenization-deferred.md` for the full ADR.
 *
 * This hook is **pure client-side** — it derives state from the already-
 * fetched `Subscription` document and `SUBSCRIPTION_CONFIG.graceDays`.
 * No new API endpoint, no extra round-trip. The backend cron
 * (`SubscriptionCronService.runSweep`) keeps minting renewal Billings on
 * schedule; this hook just decides whether the header should call them
 * out.
 *
 * Hidden cases (returns `shouldRenew: false`):
 * - `subscription === null` — implicit free tier, no renewal exists
 * - `status === 'canceled'` — terminal; access already winding down
 * - `status === 'paused'` — admin-paused; no renewals attempted
 * - period still in the future
 *
 * @see useBillingOverviewManager — shares the same `graceEndsAt` math
 */
export function useSubscriptionRenewalGuard(): SubscriptionRenewalGuardState {
  const subscriptionQuery = useSubscriptionMineApi();
  const sub = subscriptionQuery.data;
  if (!sub) return INACTIVE;
  if (sub.status === 'canceled' || sub.status === 'paused') return INACTIVE;

  const now = Date.now();
  const periodEnd = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).getTime() : undefined;
  const trialEnd = sub.trialEndsAt ? new Date(sub.trialEndsAt).getTime() : undefined;

  const trialElapsed = sub.status === 'trialing' && trialEnd !== undefined && trialEnd < now;
  const periodElapsed = periodEnd !== undefined && periodEnd < now;

  if (!trialElapsed && !periodElapsed) return INACTIVE;

  // Past-due grace math. `pastDueSince` is set by the cron the moment
  // a renewal charge fails; we add `graceDays` to get the paywall cutoff.
  // Match the same arithmetic as `useBillingOverviewManager.graceEndsAt`
  // so the two surfaces never disagree about when access ends.
  const pastDueSince = sub.pastDueSince ? new Date(sub.pastDueSince) : undefined;
  const graceEndsAt = pastDueSince
    ? new Date(pastDueSince.getTime() + SUBSCRIPTION_CONFIG.graceDays * MS_PER_DAY)
    : undefined;

  if (sub.status === 'past_due') {
    if (graceEndsAt && graceEndsAt.getTime() > now) {
      return {
        shouldRenew: true,
        severity: 'soft',
        graceEndsAt,
        reason: 'past_due_grace',
      };
    }
    return {
      shouldRenew: true,
      severity: 'hard',
      graceEndsAt,
      reason: 'past_due_overdue',
    };
  }

  // `trialing` with elapsed `trialEndsAt`, OR `active` with elapsed
  // `currentPeriodEnd`. Both are soft — the user isn't blocked yet,
  // the cron just hasn't transitioned them to `past_due`.
  return {
    shouldRenew: true,
    severity: 'soft',
    graceEndsAt: undefined,
    reason: trialElapsed ? 'trial_ended' : 'period_ended',
  };
}
